import React, { useState, useEffect } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendEmailVerification,
} from "firebase/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { auth, db } from "../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { ROLES, ROLE_LABELS } from "../lib/auth/roles";
import { DEFAULT_SCHOOL_ID } from "../lib/auth/schoolContext";
import { useSchools } from "../lib/data/useSchools";
import { requestJoinSchool } from "../lib/functions";
import { getFunctions, httpsCallable } from "firebase/functions";

const AuthForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLogin, setIsLogin] = useState(true);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [resetDisabledUntil, setResetDisabledUntil] = useState(0);
  const [verifyDisabledUntil, setVerifyDisabledUntil] = useState(0);
  const { schools, loading: loadingSchools } = useSchools();
  const [selectedSchoolId, setSelectedSchoolId] = useState("");
  const [requestedRole, setRequestedRole] = useState(ROLES.TEACHER);
  const [consentGiven, setConsentGiven] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Set initial mode based on URL params
  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode === 'signup') {
      setIsLogin(false);
    } else if (mode === 'auth') {
      setIsLogin(true);
    }
  }, [searchParams]);

  // Redirect if already logged in - check user type to go to correct dashboard
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        console.log("👤 Logged in as:", currentUser.uid);
        setUser(currentUser);
        
        // Check if this is a solo user
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.userType === 'solo' || userData.role === 'solo_user') {
              navigate("/solo/dashboard");
              return;
            }
          }
        } catch (e) {
          console.log('Could not check user type, defaulting to school dashboard');
        }
        
        navigate("/dashboard");
      } else {
        console.log("🚫 No user logged in");
        setUser(null);
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    // GDPR: require consent on signup
    if (!isLogin && !consentGiven) {
      alert("Please confirm you have a lawful basis to process student SEN data before creating an account.");
      return;
    }

    setLoading(true);

    try {
      let userCredential;
      if (isLogin) {
        userCredential = await signInWithEmailAndPassword(auth, email, password);
      } else {
        userCredential = await createUserWithEmailAndPassword(auth, email, password);
        console.log("✅ User signed up successfully");

        // Immediately request to join selected school with chosen role
        try {
          const schoolId = selectedSchoolId || (schools[0]?.id || "");
          if (!schoolId) {
            console.warn('No school selected during signup. Skipping join request.');
          } else {
            await requestJoinSchool({ requestedSchoolId: schoolId, requestedRole });
          }
        } catch (e) {
          console.warn('Failed to submit join request:', e?.message || e);
        }
      }

      const u = userCredential.user;
      // Ensure a minimal user doc exists with default TA role (self-create allowed by rules)
      try {
        const userRef = doc(db, 'users', u.uid);
        const existing = await getDoc(userRef);
        if (!existing.exists()) {
          await setDoc(userRef, {
            role: ROLES.TA,
            email: u.email || '',
            schoolId: DEFAULT_SCHOOL_ID,
            createdAt: new Date(),
          });
        } else {
          // Check if existing user is a solo user
          const userData = existing.data();
          if (userData.userType === 'solo' || userData.role === 'solo_user') {
            setUser(u);
            navigate("/solo/dashboard");
            return;
          }
        }
      } catch (e) {
        console.warn('Could not ensure user role document:', e?.message || e);
      }
      setUser(u);
      navigate("/dashboard");
    } catch (error) {
      console.error("❌ Auth error:", error.message);
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    setUser(null);
    console.log("👋 Signed out");
    navigate("/");
  };

  const handlePasswordReset = async () => {
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      alert('Enter your email above, then click "Forgot password?"');
      return;
    }
    try {
      // Use custom branded email via SendGrid
      const functions = getFunctions(undefined, 'europe-west3');
      const generateResetLink = httpsCallable(functions, 'generatePasswordResetLinkFunc');
      await generateResetLink({ email });
      
      // Start 30s cooldown
      setResetDisabledUntil(Date.now() + 30_000);
      // Navigate to confirmation screen
      navigate(`/reset-sent?email=${encodeURIComponent(email)}`);
    } catch (e) {
      console.error('Reset error:', e);
      // Still navigate to reset-sent for security (don't reveal if email exists)
      setResetDisabledUntil(Date.now() + 30_000);
      navigate(`/reset-sent?email=${encodeURIComponent(email)}`);
    }
  };

  const canSendReset = Date.now() >= resetDisabledUntil;
  const resetSecondsLeft = Math.max(0, Math.ceil((resetDisabledUntil - Date.now()) / 1000));

  const handleResendVerification = async () => {
    try {
      if (!auth.currentUser) {
        alert('Sign in first to resend verification email.');
        return;
      }
      await sendEmailVerification(auth.currentUser, { url: window.location.origin + '/login' });
      setVerifyDisabledUntil(Date.now() + 30_000);
      alert('Verification email sent. Please check your inbox.');
    } catch (e) {
      console.error('Verification error:', e);
      alert(e?.message || 'Failed to send verification email');
    }
  };
  const canSendVerify = Date.now() >= verifyDisabledUntil;
  const verifySecondsLeft = Math.max(0, Math.ceil((verifyDisabledUntil - Date.now()) / 1000));

  // If user is logged in, show logout section
  if (user) {
    return (
      <div className="flex min-h-full flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="mt-6 text-center text-2xl/9 font-bold tracking-tight text-gray-900 dark:text-white">
            Welcome back!
          </h2>
        </div>
        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
          <div className="bg-white px-6 py-12 shadow-sm sm:rounded-lg sm:px-12 dark:bg-gray-800/50 dark:shadow-none dark:outline dark:-outline-offset-1 dark:outline-white/10">
              <div className="text-center space-y-4">
                <p className="text-gray-900 dark:text-white">Logged in as {user.email}</p>
                {!user.emailVerified && (
                  <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                    Your email is not verified.
                    <div className="mt-2">
                      <button
                        onClick={handleResendVerification}
                        disabled={!canSendVerify}
                        className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
                      >
                        {canSendVerify ? 'Resend verification email' : `Resend in ${verifySecondsLeft}s`}
                      </button>
                  </div>
                  </div>
                )}
                <button
                  onClick={handleLogout}
                  className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:bg-indigo-500 dark:shadow-none dark:hover:bg-indigo-400 dark:focus-visible:outline-indigo-500"
                >
                  Log Out
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex min-h-full flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3 min-w-0">
              <img
                src="/brand/logo-thumbnail-blue.png"
                alt=""
                aria-hidden="true"
                className="h-14 w-14 shrink-0 rounded-xl"
              />
              <span className="font-bold text-2xl text-gray-900 dark:text-white truncate">
                SEN-Ed
              </span>
            </div>
            <button
              onClick={() => navigate('/')}
              className="shrink-0 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              ← Back to home
            </button>
          </div>
          <h2 className="text-center text-2xl/9 font-bold tracking-tight text-gray-900 dark:text-white">
            {isLogin ? "Sign in to your account" : "Create your account"}
          </h2>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
          <div className="bg-white px-6 py-12 shadow-sm sm:rounded-lg sm:px-12 dark:bg-gray-800/50 dark:shadow-none dark:outline dark:-outline-offset-1 dark:outline-white/10">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm/6 font-medium text-gray-900 dark:text-white">
                  Email address
                </label>
                <div className="mt-2">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-base text-gray-900 placeholder:text-gray-400 focus:border-indigo-600 focus:outline-none focus:ring-1 focus:ring-indigo-600 sm:text-sm/6 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-gray-500 dark:focus:border-indigo-500 dark:focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm/6 font-medium text-gray-900 dark:text-white">
                  Password
                </label>
                <div className="mt-2">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    autoComplete={isLogin ? "current-password" : "new-password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-base text-gray-900 placeholder:text-gray-400 focus:border-indigo-600 focus:outline-none focus:ring-1 focus:ring-indigo-600 sm:text-sm/6 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-gray-500 dark:focus:border-indigo-500 dark:focus:ring-indigo-500"
                  />
                </div>
              </div>

              {!isLogin && (
                <>
                  <div>
                    <label className="block text-sm/6 font-medium text-gray-900 dark:text-white">School</label>
                    <div className="mt-2">
                      <select
                        value={selectedSchoolId}
                        onChange={(e) => setSelectedSchoolId(e.target.value)}
                        required
                        className="block w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-base text-gray-900 focus:border-indigo-600 focus:outline-none focus:ring-1 focus:ring-indigo-600 sm:text-sm/6 dark:border-white/10 dark:bg-white/5 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-indigo-500"
                      >
                        <option value="" disabled>{loadingSchools ? 'Loading schools…' : 'Select your school'}</option>
                        {schools.map(s => (
                          <option key={s.id} value={s.id} title={s.schoolName || s.id}>{s.abbr || s.schoolName || s.id}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm/6 font-medium text-gray-900 dark:text-white">Requested Role</label>
                    <div className="mt-2">
                      <select
                        value={requestedRole}
                        onChange={(e) => setRequestedRole(e.target.value)}
                        className="block w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-base text-gray-900 focus:border-indigo-600 focus:outline-none focus:ring-1 focus:ring-indigo-600 sm:text-sm/6 dark:border-white/10 dark:bg-white/5 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-indigo-500"
                      >
                        <option value={ROLES.TEACHER}>{ROLE_LABELS[ROLES.TEACHER]}</option>
                        <option value={ROLES.TA}>{ROLE_LABELS[ROLES.TA]}</option>
                        <option value={ROLES.SENCO}>{ROLE_LABELS[ROLES.SENCO]}</option>
                      </select>
                    </div>
                  </div>
                </>
              )}

              {isLogin && (
                <div className="flex items-center justify-between">
                  <div className="flex gap-3">
                    <div className="flex h-6 shrink-0 items-center">
                      <div className="group grid size-4 grid-cols-1">
                        <input
                          id="remember-me"
                          name="remember-me"
                          type="checkbox"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 dark:border-white/10 dark:bg-white/5 dark:checked:border-indigo-500 dark:checked:bg-indigo-500 dark:indeterminate:border-indigo-500 dark:indeterminate:bg-indigo-500 dark:focus-visible:outline-indigo-500 forced-colors:appearance-auto"
                        />
                        <svg
                          fill="none"
                          viewBox="0 0 14 14"
                          className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25 dark:group-has-disabled:stroke-white/25"
                        >
                          <path
                            d="M3 8L6 11L11 3.5"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="opacity-0 group-has-checked:opacity-100"
                          />
                          <path
                            d="M3 7H11"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="opacity-0 group-has-indeterminate:opacity-100"
                          />
                        </svg>
                      </div>
                    </div>
                    <label htmlFor="remember-me" className="block text-sm/6 text-gray-900 dark:text-white">
                      Remember me
                    </label>
                  </div>

                  <div className="text-sm/6">
                    <button
                      type="button"
                      onClick={canSendReset ? handlePasswordReset : undefined}
                      disabled={!canSendReset}
                      className="font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 disabled:opacity-50"
                    >
                      {canSendReset ? 'Forgot password?' : `Reset link sent (${resetSecondsLeft}s)`}
                    </button>
                  </div>
                </div>
              )}

              {/* GDPR consent — shown only on signup */}
              {!isLogin && (
                <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <input
                    id="gdpr-consent"
                    type="checkbox"
                    checked={consentGiven}
                    onChange={(e) => setConsentGiven(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600"
                    required
                  />
                  <label htmlFor="gdpr-consent" className="text-xs text-gray-700">
                    I confirm I am a registered professional working in education and that I have a
                    lawful basis (e.g. legal obligation or public task) to process student SEN data
                    on this platform. I have read and agree to the{' '}
                    <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline">Privacy Policy</a>{' '}
                    and{' '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline">Terms of Service</a>.
                  </label>
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={loading || (!isLogin && !consentGiven)}
                  className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:bg-indigo-500 dark:shadow-none dark:hover:bg-indigo-400 dark:focus-visible:outline-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Please wait..." : isLogin ? "Sign in" : "Sign up"}
                </button>
              </div>
            </form>
          </div>

          <p className="mt-10 text-center text-sm/6 text-gray-500 dark:text-gray-400">
            {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 cursor-pointer"
            >
              {isLogin ? "Sign up" : "Sign in"}
            </button>
          </p>
          
          {/* Solo account link */}
          <p className="mt-4 text-center text-sm text-gray-400 dark:text-gray-500">
            Not part of a school?{" "}
            <button
              onClick={() => navigate('/signup/solo')}
              className="text-purple-600 hover:text-purple-500 dark:text-purple-400 dark:hover:text-purple-300"
            >
              Get individual access →
            </button>
          </p>
        </div>
      </div>
    </>
  );
};

export default AuthForm;
